/**
 * Skill 云端同步（docs/10 §2.10-①：去掉本地兜底语义）。
 *
 * - 会话注入：登录后由 session.ts 调 setSkillCloudTenant 提供租户；token 从 platform/api 读
 *   （内存 + httpOnly refresh cookie，不再依赖 localStorage 镜像）。
 * - 拉取（syncSkillsFromCloud）：登录态下失败就是失败——抛错，绝不用本机残留缓存顶替；
 *   成功返回完整清单与版本历史，由调用方灌入 SkillRegistry（seedFromCloud）。
 * - 推送（pushSkillActionToCloud）：本机内存写入后尽力同步一份到云端；失败 console.error
 *   （可见错误），不静默吞掉——云端是唯一数据源，刷新后只有云端那份算数。
 */
import type { SkillManifest } from './types'
import type { SkillHistoryEntry } from './skillRegistry'
import { apiRequest, getAccessToken } from '@/platform/api'

let cloudTenantPublicId: string | null = null

/** 登录态下由 platform/session 注入当前租户 public_id；登出置 null */
export function setSkillCloudTenant(tenantPublicId: string | null): void {
  cloudTenantPublicId = tenantPublicId
}

export function isSkillCloudReady(): boolean {
  return !!getAccessToken() && !!cloudTenantPublicId
}

export interface SkillCloudSession {
  accessToken: string
  tenantPublicId: string
}

export function readSkillCloudSession(): SkillCloudSession | null {
  const token = getAccessToken()
  if (!token || !cloudTenantPublicId) return null
  return { accessToken: token, tenantPublicId: cloudTenantPublicId }
}

interface CloudSkillListItem {
  skillKey: string
  title: string
  description?: string
  origin: string
  status: string
  headVersion: number
  updatedAt: string
}

interface CloudSkillDetail extends CloudSkillListItem {
  manifest: SkillManifest
  versions: Array<{ version: number; manifest: SkillManifest; createdAt: string }>
}

/** tenantId 由 api.ts 的 request 统一处理鉴权，这里只需拼 query */
function apiFetch<T>(path: string, session: SkillCloudSession, init?: RequestInit): Promise<T> {
  const q = path.includes('?') ? '&' : '?'
  return apiRequest<T>(`${path}${q}tenantId=${encodeURIComponent(session.tenantPublicId)}`, init)
}

export interface CloudSyncResult {
  synced: number
  manifests: SkillManifest[]
  historyByKey: Record<string, SkillHistoryEntry[]>
}

/**
 * 从云端拉取本租户全部 Skill + 版本历史。
 * 未登录：返回 null（调用方按无云处理）；已登录但失败：抛错（不静默复用本机缓存）。
 */
export async function syncSkillsFromCloud(): Promise<CloudSyncResult | null> {
  const session = readSkillCloudSession()
  if (!session) return null
  const list = await apiFetch<CloudSkillListItem[]>('/skills', session)
  const manifests: SkillManifest[] = []
  const historyByKey: Record<string, SkillHistoryEntry[]> = {}
  for (const item of list) {
    const detail = await apiFetch<CloudSkillDetail>(`/skills/${encodeURIComponent(item.skillKey)}`, session)
    manifests.push(detail.manifest)
    historyByKey[item.skillKey] = detail.versions
      .slice()
      .reverse()
      .map((v) => ({ version: String(v.version), manifest: v.manifest, atMs: new Date(v.createdAt).getTime() }))
  }
  return { synced: manifests.length, manifests, historyByKey }
}

/** 本机内存写入成功后，尽力同步一份到云端；失败抛错由调用方决定如何呈现（不静默） */
export async function pushSkillActionToCloud(
  action: { kind: string; manifest: SkillManifest; targetId?: string; version?: string },
): Promise<void> {
  const session = readSkillCloudSession()
  if (!session) return
  try {
    switch (action.kind) {
      case 'create':
      case 'install':
        await apiFetch('/skills', session, { method: 'POST', body: JSON.stringify({ manifest: action.manifest }) })
        break
      case 'update':
      case 'rollback':
        // rollback 在本机是「恢复旧内容、语义上仍是一次新写入」；云端同样按整包 patch 处理
        await apiFetch(`/skills/${encodeURIComponent(action.manifest.id)}`, session, {
          method: 'PATCH',
          body: JSON.stringify({ patch: action.manifest }),
        })
        break
      case 'disable':
        if (action.targetId) {
          await apiFetch(`/skills/${encodeURIComponent(action.targetId)}/disable`, session, { method: 'POST' })
        }
        break
    }
  } catch (err) {
    // 云端是唯一数据源：推送失败不是「多一份没同步」的小事，必须可见
    console.error(`[skillCloud] Skill 动作推送到云端失败（kind=${action.kind}），刷新后该改动不会保留`, err)
    throw err
  }
}
